import React, { useState, useRef, useEffect } from 'react';
import { Filter, ChevronDown, Star, MapPin, Calendar, DollarSign } from 'lucide-react';

const FilterDropdown = ({
  filters = {},
  onFiltersChange,
  options = [],
  placeholder = "Filter results",
  showApplyButton = true,
  showClearButton = true,
  size = 'md',
  className = "",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const dropdownRef = useRef(null);

  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-5 py-3 text-lg'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  // Only sync when dropdown closes or filters prop changes meaningfully
  useEffect(() => {
    if (!isOpen) {
      setLocalFilters(filters);
    }
  }, [isOpen]); // Removed filters from deps to prevent infinite loop

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getActiveFilterCount = () => {
    return Object.values(localFilters).filter(value => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    }).length;
  };

  const handleFilterChange = (key, value, type = 'single') => {
    setLocalFilters(prev => {
      const newFilters = { ...prev };
      
      if (type === 'multiple') {
        if (!newFilters[key]) newFilters[key] = [];
        const currentValues = [...newFilters[key]];
        const index = currentValues.indexOf(value);
        
        if (index > -1) {
          currentValues.splice(index, 1);
        } else {
          currentValues.push(value);
        }
        
        newFilters[key] = currentValues;
      } else {
        newFilters[key] = value;
      }
      
      return newFilters;
    });

    // Auto-apply if no apply button
    if (!showApplyButton) {
      const newFilters = { ...localFilters };
      if (type === 'multiple') {
        if (!newFilters[key]) newFilters[key] = [];
        const currentValues = [...newFilters[key]];
        const index = currentValues.indexOf(value);
        if (index > -1) {
          currentValues.splice(index, 1);
        } else {
          currentValues.push(value);
        }
        newFilters[key] = currentValues;
      } else {
        newFilters[key] = value;
      }
      onFiltersChange(newFilters);
    }
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
    setIsOpen(false);
  };

  const clearFilters = () => {
    const clearedFilters = {};
    Object.keys(localFilters).forEach(key => {
      clearedFilters[key] = Array.isArray(localFilters[key]) ? [] : '';
    });
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  const renderFilterOption = (option) => {
    const { key, label, type, options: subOptions, min, max, step, icon: OptionIcon } = option;
    const currentValue = localFilters[key];

    switch (type) {
      case 'select':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {OptionIcon && <OptionIcon className="inline w-4 h-4 mr-2" />}
              {label}
            </label>
            <select
              value={currentValue || ''}
              onChange={(e) => handleFilterChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="">All {label}</option>
              {subOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'multiselect':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {OptionIcon && <OptionIcon className="inline w-4 h-4 mr-2" />}
              {label}
            </label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {subOptions.map(opt => (
                <label key={opt.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={currentValue?.includes(opt.value) || false}
                    onChange={() => handleFilterChange(key, opt.value, 'multiple')}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">{opt.label}</span>
                  {opt.count !== undefined && (
                    <span className="ml-auto text-xs text-gray-500">({opt.count})</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        );

      case 'range':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {OptionIcon && <OptionIcon className="inline w-4 h-4 mr-2" />}
              {label}
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Min"
                min={min}
                max={max}
                step={step}
                value={currentValue?.min || ''}
                onChange={(e) => handleFilterChange(key, {
                  ...currentValue,
                  min: e.target.value
                })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                placeholder="Max"
                min={min}
                max={max}
                step={step}
                value={currentValue?.max || ''}
                onChange={(e) => handleFilterChange(key, {
                  ...currentValue,
                  max: e.target.value
                })}
                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-500"
              />
            </div>
          </div>
        );

      case 'rating':
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {OptionIcon && <OptionIcon className="inline w-4 h-4 mr-2" />}
              {label}
            </label>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map(rating => (
                <label key={rating} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="radio"
                    name={key}
                    checked={currentValue === rating}
                    onChange={() => handleFilterChange(key, rating)}
                    className="text-red-600 focus:ring-red-500"
                  />
                  <div className="ml-2 flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="ml-1 text-sm text-gray-700">
                      {rating} star{rating !== 1 ? 's' : ''} & up
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div key={key} className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {OptionIcon && <OptionIcon className="inline w-4 h-4 mr-2" />}
              {label}
            </label>
            <input
              type="text"
              value={currentValue || ''}
              onChange={(e) => handleFilterChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          </div>
        );
    }
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center justify-between w-full ${sizeClasses[size]} border border-gray-300 rounded-md
          bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
          disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200
        `}
      >
        <div className="flex items-center space-x-2">
          <Filter className={iconSizes[size]} />
          <span>{placeholder}</span>
          {activeFilterCount > 0 && (
            <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {activeFilterCount}
            </span>
          )}
        </div>
        <ChevronDown className={`${iconSizes[size]} transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-80">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Filters</h3>
              {showClearButton && activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto pr-2">
              {options.map(renderFilterOption)}
            </div>

            {showApplyButton && (
              <div className="flex space-x-2 pt-4 border-t border-gray-200 mt-4">
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Predefined filter configurations for common use cases
export const hospitalFilters = [
  {
    key: 'specialization',
    label: 'Specialization',
    type: 'multiselect',
    icon: Star,
    options: [
      { value: 'cardiology', label: 'Cardiology', count: 12 },
      { value: 'neurology', label: 'Neurology', count: 8 },
      { value: 'pediatrics', label: 'Pediatrics', count: 15 },
      { value: 'orthopedics', label: 'Orthopedics', count: 10 },
      { value: 'oncology', label: 'Oncology', count: 6 }
    ]
  },
  {
    key: 'rating',
    label: 'Minimum Rating',
    type: 'rating',
    icon: Star
  },
  {
    key: 'distance',
    label: 'Distance (km)',
    type: 'range',
    icon: MapPin,
    min: 0,
    max: 50,
    step: 1
  },
  {
    key: 'availability',
    label: 'Availability',
    type: 'select',
    icon: Calendar,
    options: [
      { value: 'open_now', label: 'Open Now' },
      { value: '24_hours', label: '24 Hours' },
      { value: 'emergency', label: 'Emergency Services' }
    ]
  }
];

export const pharmacyFilters = [
  {
    key: 'services',
    label: 'Services',
    type: 'multiselect',
    icon: Star,
    options: [
      { value: 'prescription', label: 'Prescription Drugs', count: 25 },
      { value: 'otc', label: 'Over-the-Counter', count: 30 },
      { value: 'delivery', label: 'Home Delivery', count: 18 },
      { value: 'consultation', label: 'Pharmacist Consultation', count: 12 },
      { value: 'medical_devices', label: 'Medical Devices', count: 8 }
    ]
  },
  {
    key: 'rating',
    label: 'Minimum Rating',
    type: 'rating',
    icon: Star
  },
  {
    key: 'distance',
    label: 'Distance (km)',
    type: 'range',
    icon: MapPin,
    min: 0,
    max: 25,
    step: 0.5
  },
  {
    key: 'price_range',
    label: 'Price Range',
    type: 'select',
    icon: DollarSign,
    options: [
      { value: 'budget', label: 'Budget Friendly' },
      { value: 'moderate', label: 'Moderate' },
      { value: 'premium', label: 'Premium' }
    ]
  }
];

export const doctorFilters = [
  {
    key: 'specialization',
    label: 'Specialization',
    type: 'multiselect',
    icon: Star,
    options: [
      { value: 'general_practice', label: 'General Practice', count: 45 },
      { value: 'cardiology', label: 'Cardiology', count: 12 },
      { value: 'dermatology', label: 'Dermatology', count: 18 },
      { value: 'pediatrics', label: 'Pediatrics', count: 22 },
      { value: 'psychiatry', label: 'Psychiatry', count: 8 },
      { value: 'orthopedics', label: 'Orthopedics', count: 15 }
    ]
  },
  {
    key: 'consultation_type',
    label: 'Consultation Type',
    type: 'multiselect',
    icon: Calendar,
    options: [
      { value: 'online', label: 'Online Consultation', count: 32 },
      { value: 'in_person', label: 'In-Person', count: 58 },
      { value: 'home_visit', label: 'Home Visit', count: 12 }
    ]
  },
  {
    key: 'rating',
    label: 'Minimum Rating',
    type: 'rating',
    icon: Star
  },
  {
    key: 'consultation_fee',
    label: 'Consultation Fee (KES)',
    type: 'range',
    icon: DollarSign,
    min: 500,
    max: 5000,
    step: 100
  },
  {
    key: 'availability',
    label: 'Availability',
    type: 'select',
    icon: Calendar,
    options: [
      { value: 'today', label: 'Available Today' },
      { value: 'this_week', label: 'This Week' },
      { value: 'next_week', label: 'Next Week' }
    ]
  }
];

export default FilterDropdown;